import type { ConsentStrategy } from './ConsentStrategy';

declare global {
  interface Window {
    /** consentmanager.net's CMP API — third-party, narrowed at runtime. */
    __cmp?: unknown;
  }
}

/** Normalized vendor name a shop may declare in consentmanager to give BirdsView its own toggle. */
const CMP_VENDOR_NAME = 'birdsview';

/**
 * consentmanager's wizard creates its "classic" purposes with the fixed ids c51–c55; c54 is
 * Measurement, where shops file their analytics tools. c51 (Function) is granted before the
 * visitor answers the banner, so only c54 may stand in for consent.
 */
const MEASUREMENT_PURPOSE_ID = 'c54';

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : null;
}

/**
 * Until the CMP has loaded, `__cmp` is a stub that queues commands and returns undefined —
 * that reads as absent, so the strategies behind this one decide for the tick. `__cmp` is also
 * the legacy IAB TCF v1 global other CMPs still ship, so an object without consentmanager's
 * consent maps reads as absent too, instead of claiming the page for a foreign CMP.
 */
function readCmpData(): Record<string, unknown> | null {
  const cmp = window.__cmp;
  if (typeof cmp !== 'function') {
    return null;
  }

  const data = asRecord((cmp as (command: string) => unknown)('getCMPData'));
  if (data === null || (asRecord(data.purposeConsents) === null && asRecord(data.vendorConsents) === null)) {
    return null;
  }

  return data;
}

/**
 * Vendor ids are per-account (`c12345` for custom vendors), so the shop's BirdsView vendor can
 * only be found by name. `vendorsList` shows up in live `getCMPData` output but is missing from
 * consentmanager's documented fields; should it go away, the lookup misses and the Measurement
 * purpose decides.
 */
function findBirdsviewVendorId(vendorsList: unknown): string | null {
  if (!Array.isArray(vendorsList)) {
    return null;
  }

  for (const vendor of vendorsList as unknown[]) {
    const { id, name } = asRecord(vendor) ?? {};
    if (typeof name === 'string' && name.toLowerCase().replace(/\s+/g, '') === CMP_VENDOR_NAME) {
      return typeof id === 'string' || typeof id === 'number' ? String(id) : null;
    }
  }

  return null;
}

/**
 * A declared BirdsView vendor is the shop's explicit choice and decides alone. Without one we
 * follow the Measurement purpose, so shops that never configured BirdsView in consentmanager
 * still track exactly when their other analytics tools may. Neither available means no
 * consent; BirdsView has no IAB vendor id, so TCF-purpose setups must declare the vendor.
 */
export class ConsentmanagerNetStrategy implements ConsentStrategy {
  readonly name = 'consentmanager-net';

  isPresent(): boolean {
    return readCmpData() !== null;
  }

  hasConsent(): boolean {
    const data = readCmpData();
    if (data === null) {
      return false;
    }

    const vendorId = findBirdsviewVendorId(data.vendorsList);
    if (vendorId !== null) {
      return asRecord(data.vendorConsents)?.[vendorId] === true;
    }

    return asRecord(data.purposeConsents)?.[MEASUREMENT_PURPOSE_ID] === true;
  }
}
